// src/pages/CapturePage/blocks/FastTalkingGame.js
//
// Diadochokinetic tasks, as a game: pa · ta · ka · pa-ta-ka, eight seconds each.
//
// Nothing here is scored or labelled. The child sees a countdown ring and a
// mascot; the rate is computed later, in the score sheet, from the recording.
//
// "Could not attempt" is a first-class outcome, not a failure to record. A
// four-year-old who cannot sequence pa-ta-ka is a different finding from one who
// manages it slowly, and the two must never end up as the same row.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import BlockExit from '../BlockExit';
import { captureError, saveDdkTask } from '../captureApi';
import { startRawRecording } from '../rawRecorder';

const AGE_NOTE_FALLBACK = 'Younger children often cannot sequence this.';

export default function FastTalkingGame({ session, block, ageMonths, onSaved, onFinish }) {
    const tasks = block.tasks || [];
    // Two attempts at each sound. With one there is no way to tell a spoiled
    // sample from a slow child: a cough, a false start and a child who simply
    // does better the second time all look alike.
    const trialsPerTask = block.trials_per_task || 1;

    // What is already recorded, so a session resumed after a break picks up at
    // the right attempt rather than starting the sound again.
    const recorded = {};
    (session.ddk || []).forEach((row) => {
        recorded[row.task] = recorded[row.task] || { trials: new Set(), refused: false };
        recorded[row.task].trials.add(row.trial || 1);
        if (!row.attempted) recorded[row.task].refused = true;
    });
    const taskDone = (key) => {
        const seen = recorded[key];
        // A refusal finishes the sound in one row: "could not sequence it" does
        // not become truer twice.
        return Boolean(seen && (seen.refused || seen.trials.size >= trialsPerTask));
    };

    const firstUnfinished = tasks.findIndex((t) => !taskDone(t.key));
    const [index, setIndex] = useState(() => (firstUnfinished === -1 ? 0 : firstUnfinished));
    const [trial, setTrial] = useState(() => {
        const start = tasks[firstUnfinished === -1 ? 0 : firstUnfinished];
        const seen = start && recorded[start.key];
        return seen ? Math.min(seen.trials.size + 1, trialsPerTask) : 1;
    });
    const [phase, setPhase] = useState('ready');   // ready | recording | saving
    const [remaining, setRemaining] = useState(block.seconds_per_task || 8);
    const [level, setLevel] = useState(0);
    const [error, setError] = useState('');
    const [askingNa, setAskingNa] = useState(false);
    const [naReason, setNaReason] = useState('');
    const recorderRef = useRef(null);
    const tickRef = useRef(null);

    const task = tasks[index];
    const tooYoung = task?.min_age_months != null && ageMonths != null && ageMonths < task.min_age_months;

    const clearTick = () => {
        if (tickRef.current) { clearInterval(tickRef.current); tickRef.current = null; }
    };
    useEffect(() => clearTick, []);

    // Next attempt at this sound, or the next sound, or the end of the block.
    const advance = useCallback((finishedTask = false) => {
        setPhase('ready');
        setError('');
        setLevel(0);
        setRemaining(block.seconds_per_task || 8);

        if (!finishedTask && trial < trialsPerTask) {
            setTrial(trial + 1);
            return;
        }
        setTrial(1);
        setIndex((i) => {
            if (i + 1 < tasks.length) return i + 1;
            onFinish();
            return i;
        });
    }, [block.seconds_per_task, onFinish, tasks.length, trial, trialsPerTask]);

    const finishRecording = useCallback(async () => {
        clearTick();
        const recorder = recorderRef.current;
        recorderRef.current = null;
        if (!recorder) return;

        setPhase('saving');
        try {
            const audio = await recorder.stop();
            const r = await saveDdkTask(session.id, task.key, {
                trial,
                attempted: true,
                audio_base64: audio.base64,
                duration_ms: audio.durationMs,
                sample_rate_hz: audio.sampleRate,
            });
            onSaved(r.data);
            advance();
        } catch (e) {
            setError(captureError(e, 'Could not save that recording. Try the task again.'));
            setPhase('ready');
        }
    }, [advance, onSaved, session.id, task, trial]);

    const start = async () => {
        setError('');
        try {
            recorderRef.current = await startRawRecording({ onLevel: setLevel });
        } catch (e) {
            setError('The microphone is not available. Check the permission and try again.');
            return;
        }
        setPhase('recording');
        const total = block.seconds_per_task || 8;
        setRemaining(total);
        const startedAt = Date.now();
        tickRef.current = setInterval(() => {
            const left = total - Math.floor((Date.now() - startedAt) / 1000);
            if (left <= 0) finishRecording();
            else setRemaining(left);
        }, 200);
    };

    const markNotAttempted = async () => {
        // No generic fallback. "Could not attempt" is read later as a clinical
        // finding, and a reason nobody wrote is worth nothing to the reader.
        if (!naReason.trim()) return;
        setPhase('saving');
        try {
            const r = await saveDdkTask(session.id, task.key, {
                trial,
                attempted: false,
                na_reason: naReason.trim(),
            });
            onSaved(r.data);
            setAskingNa(false);
            setNaReason('');
            // A refusal finishes the sound: no second attempt at something the
            // child could not do once.
            advance(true);
        } catch (e) {
            setError(captureError(e, 'Could not save that. Try again.'));
            setPhase('ready');
        }
    };

    if (!task) return null;

    const total = block.seconds_per_task || 8;
    const ring = phase === 'recording' ? 1 - remaining / total : 0;

    return (
        <div className="cap-child-screen">
            <div className="cap-child-head">
                <span className="cap-child-step">
                    {index + 1} / {tasks.length}
                    {trialsPerTask > 1 ? ` · try ${trial} of ${trialsPerTask}` : ''}
                </span>
                <h2>{block.child_title?.hi}</h2>
                <p>{block.child_title?.en}</p>
            </div>

            <div className={`cap-ring${phase === 'recording' ? ' live' : ''}`}
                 style={{ '--ring': ring, '--level': Math.min(1, level * 3) }}>
                <div className="cap-ring-inner">
                    <span className="cap-prompt">{task.prompt}</span>
                    <span className="cap-prompt-en">{task.en}</span>
                </div>
            </div>

            {phase === 'recording' ? (
                <p className="cap-big">{remaining}</p>
            ) : (
                <p className="cap-instruction">{block.instruction?.hi} <em>{block.instruction?.en}</em></p>
            )}

            {tooYoung && phase === 'ready' && !askingNa ? (
                <p className="cap-note">{task.skip_note?.en || AGE_NOTE_FALLBACK}</p>
            ) : null}

            {error ? <p className="cap-error">{error}</p> : null}

            {phase === 'ready' && !askingNa ? (
                <div className="cap-actions">
                    <button type="button" className="cap-btn big" onClick={start}>Start</button>
                    <button type="button" className="cap-btn ghost" onClick={() => setAskingNa(true)}>
                        Could not attempt
                    </button>
                </div>
            ) : null}

            {askingNa ? (
                <div className="cap-na">
                    <label htmlFor="cap-na-reason">Why could this task not be attempted?</label>
                    <textarea
                        id="cap-na-reason" rows="2" value={naReason}
                        onChange={(e) => setNaReason(e.target.value)}
                        placeholder="Could not sequence the three sounds; refused; too tired…"
                    />
                    <p className="cap-hint">
                        This is recorded as “not attempted”, which is not the same as a slow rate.
                    </p>
                    <div className="cap-actions">
                        <button
                            type="button" className="cap-btn"
                            onClick={markNotAttempted} disabled={!naReason.trim()}
                        >Save</button>
                        <button type="button" className="cap-btn ghost" onClick={() => setAskingNa(false)}>
                            Back
                        </button>
                    </div>
                </div>
            ) : null}

            {phase === 'saving' ? <p className="cap-hint">Saving…</p> : null}

            {phase === 'recording' ? (
                <BlockExit onExit={finishRecording} label="Hold to stop" />
            ) : null}
        </div>
    );
}
